import { Component, HostBinding, Input, OnDestroy, OnInit, SimpleChanges, OnChanges } from '@angular/core';
import { ControlValueAccessor, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { Subject, Subscription } from 'rxjs';
import { MatFormFieldModule } from '@angular/material/form-field';
import { CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { IconComponent } from '@nagel-assests';
import { BaseChangeEvent, FieldBase, NumberRangeType } from '@nagel-types';
import { isEmptyObject } from '@nagel-utils';

export class InputNumberRangeErrorStateMatcher implements ErrorStateMatcher {
    isErrorState(control: FormControl | null): boolean {
        if (!(control?.errors))
        {
            return false;
        }

        return control!.errors && control!.errors['matInputNumberRangeParse'].text !== '';
    }
}

export type RangeNumber = {
  from: number| undefined;
  to: number | undefined;
};

@Component({
    selector: 'lib-input-number-range',
    imports: [
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        IconComponent,
        CommonModule,
        FormsModule
    ],
    templateUrl: './input-number-range.component.html',
    styleUrl: './input-number-range.component.scss'
})

export class InputNumberRangeComponent implements ControlValueAccessor, OnDestroy, OnInit, OnChanges {
    @Input() fieldLabel?: string = '';
    @Input() fieldKey?: string = '';
    @Input() className?: string = '';
    @Input() icon?: string = '';
    @Input() field?: FieldBase<unknown>;
    @Input() defaultValues?: NumberRangeType = { from: undefined, to: undefined };
    @Input() onChange?: (event: BaseChangeEvent) => void;
    inputNumberRangeForm!: FormGroup;
    stateChanges = new Subject<void>();
    focused = false;
    required = false;
    private _placeholder = '';
    static nextId = 0;
    matcher: ErrorStateMatcher = new InputNumberRangeErrorStateMatcher();

  @HostBinding() id = `mat-range-${InputNumberRangeComponent.nextId++}`;

  ngOnInit() {
      this.inputNumberRangeForm = new FormGroup({
          from: new FormControl(this.getFormControlDefaultValues().from),
          to: new FormControl(this.getFormControlDefaultValues().to)
      });
  }

  ngOnChanges(changes: SimpleChanges) {
      if (changes['defaultValues']) {
          this.value = this.defaultValues || { from: undefined, to: undefined };
      }
  }

  getFormControlDefaultValues = () => {
      return this.defaultValues || { from: undefined, to: undefined };
  };

  onInputChange(event: {target: {value: RangeNumber}}): void {
      const {value} = event.target;
      this.value = value;
      //this.onChange(value);
      this.onTouched();


  }

  handleOnChange = () => {
      const validatedFrom = this.value.from ? Math.trunc(Math.max(0, this.value.from)) : undefined;
      const validatedTo = this.value.to ? Math.trunc(Math.max(0, this.value.to)) : undefined;

      if (this.onChange) {
          this.onChange({ value: { from: validatedFrom, to: validatedTo }, key: this.fieldKey });
      }
  };

  @Input()
  set value(value: RangeNumber) {
      if (this.inputNumberRangeForm) {
          this.inputNumberRangeForm.patchValue(value);
          this.stateChanges.next();
      }
  }

  get value() {
      return this.inputNumberRangeForm.value;
  }

  @HostBinding('class.floating')
  get shouldLabelFloat() {
      return this.focused || !this.empty;
  }

  get empty() {
      const formValue = this.inputNumberRangeForm.value;
      return !(formValue.from && formValue.to);
  }

  get placeholder(): string {
      return this._placeholder;
  }

  set placeholder(value: string) {
      this._placeholder = value;
      this.stateChanges.next();
  }

  get errorState(): boolean {
      return this.inputNumberRangeForm.invalid;
  }

  onTouched = () => {};

  onChangeSubs: Subscription[] = [];

  ngOnDestroy() {
      this.stateChanges.complete();
      this.onChangeSubs.forEach(sub => sub.unsubscribe());
  }

  writeValue(value: RangeNumber): void {
      if (value && !isEmptyObject(value)) {
          this.inputNumberRangeForm.setValue(value, { emitEvent: false });
      }
      this.stateChanges.next();
  }

  registerOnChange(onChange: () => void): void {
      const subscription = this.inputNumberRangeForm.valueChanges.subscribe(onChange);
      this.onChangeSubs.push(subscription);
  }

  registerOnTouched(fn: () => void): void {
      this.onTouched = fn;
  }

  getIsDisabled(): boolean {
      return false;
  }

  getFromPlaceholder(): string {
      const { label } = this.field || {};
      const prefix = label ? $localize`${label} ` : $localize`${this.fieldLabel} `;
      const translatedFrom = $localize`from`;

      return `${prefix} ${translatedFrom}`;
  }

  getToPlaceholder(): string {
      const { label } = this.field || {};
      const prefix = label ? $localize`${label} ` : $localize`${this.fieldLabel} `;
      const translatedTo = $localize`to`;

      return `${prefix} ${translatedTo}`;
  }

  handleOnFromReset = () => {
      this.value = { from: undefined, to: this.inputNumberRangeForm.value.to || undefined};
      const resetKey = this.field?.key || this.fieldKey;

      if (this.field && this.field.onChange) {
          this.field.onChange({ value: this.inputNumberRangeForm.value, key: resetKey });
      }

      if (!this.field && this.onChange) {
          this.onChange({ value: this.inputNumberRangeForm.value, key: resetKey });
      }
  };

  handleOnToReset = () => {
      this.value = { from: this.inputNumberRangeForm.value.from, to: undefined };
      const resetKey = this.field?.key || this.fieldKey;

      if (this.field && this.field.onChange) {
          this.field.onChange({ value: this.inputNumberRangeForm.value, key: resetKey });
      }

      if (!this.field && this.onChange) {
          this.onChange({ value: this.inputNumberRangeForm.value, key: resetKey });
      }
  };
}